import React, { useState, useEffect } from 'react';
import { Users, TrendingUp, DollarSign, Clock, Filter, Download, Plus, Mail, Phone, MessageSquare, Star, MapPin, Calendar, Zap, ArrowRight, Eye, Edit, X } from 'lucide-react';

const LeadManagement = () => {
  const [selectedLead, setSelectedLead] = useState(null);
  const [showDetailsPanel, setShowDetailsPanel] = useState(false);
  const [showAddLeadModal, setShowAddLeadModal] = useState(false);
  const [filterDestination, setFilterDestination] = useState('all');
  const [filterSegment, setFilterSegment] = useState('all');
  const [filterSource, setFilterSource] = useState('all');
  const [leadsData, setLeadsData] = useState(null); // État pour gérer les données des leads
  
  // État du formulaire d'ajout de lead
  const [newLead, setNewLead] = useState({
    // Informations personnelles
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    
    // Préférences de voyage
    desiredArrival: '',
    desiredDeparture: '',
    destination: '',
    guestCount: '',
    children: '',
    
    // Détails du projet
    budget: '',
    propertyType: '',
    amenities: [],
    purposeOfStay: '',
    
    // Lead tracking
    source: '',
    notes: '',
    priority: 'medium',
    
    // Services VIP
    conciergeServices: [],
    specialRequests: ''
  });

  // Statistiques du pipeline
  const pipelineStats = {
    activeLeads: 127,
    totalPipeline: "€2.4M",
    conversionRate: 23,
    avgTime: "12 jours"
  };

  // Insights IA
  const aiInsights = {
    hotOpportunities: [
      "3 leads VIP prêts à signer cette semaine (€450K de potentiel)",
      "Forte demande pour Saint-Tropez en octobre (+40%)",
      "2 leads à haute valeur nécessitent un suivi urgent"
    ],
    detectedTrends: [
      "Augmentation des demandes pour villas avec piscine chauffée",
      "Préférence croissante pour les séjours de 10+ jours",
      "Tendance vers les destinations moins touristiques"
    ],
    urgentActions: [
      "Suivre lead #L-2024-089 (€180K) - pas de contact depuis 5 jours",
      "Proposition expire demain pour lead #L-2024-076",
      "Lead chaud détecté via IA - nouveau prospect Cannes"
    ]
  };

  // Initialiser les données des leads au premier rendu
  useEffect(() => {
    if (!leadsData) {
      setLeadsData({...leadsByStage}); // Copier les données initiales
    }
  }, []);

  // Pipeline Kanban
  const pipelineStages = [
    {
      id: 'first-contact',
      name: 'Premier Contact',
      count: 23,
      value: '€580K',
      color: 'blue'
    },
    {
      id: 'qualification',
      name: 'Qualification',
      count: 18,
      value: '€720K',
      color: 'yellow'
    },
    {
      id: 'proposal',
      name: 'Proposition',
      count: 12,
      value: '€680K',
      color: 'orange'
    },
    {
      id: 'negotiation',
      name: 'Négociation',
      count: 8,
      value: '€420K',
      color: 'purple'
    },
    {
      id: 'closing',
      name: 'Closing',
      count: 5,
      value: '€310K',
      color: 'green'
    }
  ];

  // Leads par étape
  const leadsByStage = {
    'first-contact': [
      {
        id: 'L-2024-089',
        name: 'Alexandre Dubois',
        source: 'Site Web',
        destination: 'Cannes',
        value: 28000,
        description: 'Villa luxe pour anniversaire mariage (50 ans)',
        status: 'Hot',
        lastActivity: '2024-09-12',
        aiScore: 87
      },
      {
        id: 'L-2024-090',
        name: 'Sarah Johnson',
        source: 'Référence',
        destination: 'Saint-Tropez',
        value: 45000,
        description: 'Séjour famille étendue - Noël 2024',
        status: 'VIP',
        lastActivity: '2024-09-11',
        aiScore: 92
      },
      {
        id: 'L-2024-091',
        name: 'Marco Benetti',
        source: 'Instagram',
        destination: 'Monaco',
        value: 35000,
        description: 'Villa + yacht pour événement business',
        status: 'Warm',
        lastActivity: '2024-09-10',
        aiScore: 74
      }
    ],
    'qualification': [
      {
        id: 'L-2024-076',
        name: 'Isabella Martinez',
        source: 'Publicité Google',
        destination: 'Nice',
        value: 22000,
        description: 'Retraite wellness groupe yoga',
        status: 'Hot',
        lastActivity: '2024-09-13',
        aiScore: 81
      },
      {
        id: 'L-2024-077',
        name: 'David Chen',
        source: 'Partenaire',
        destination: 'Cannes',
        value: 67000,
        description: 'Convention entreprise tech - 40 personnes',
        status: 'VIP',
        lastActivity: '2024-09-12',
        aiScore: 95
      }
    ],
    'proposal': [
      {
        id: 'L-2024-065',
        name: 'Sophie Laurent',
        source: 'Site Web',
        destination: 'Saint-Tropez',
        value: 89000,
        description: 'Mariage destination - juillet 2025',
        status: 'VIP',
        lastActivity: '2024-09-13',
        aiScore: 89
      }
    ],
    'negotiation': [
      {
        id: 'L-2024-054',
        name: 'Robert Williams',
        source: 'Référence VIP',
        destination: 'Monaco',
        value: 120000,
        description: 'Séjour estival récurrent - famille',
        status: 'VIP',
        lastActivity: '2024-09-13',
        aiScore: 96
      }
    ],
    'closing': [
      {
        id: 'L-2024-043',
        name: 'Elena Rossi',
        source: 'Retour client',
        destination: 'Cannes',
        value: 54000,
        description: 'Client fidèle - réservation annuelle',
        status: 'VIP',
        lastActivity: '2024-09-14',
        aiScore: 98
      }
    ]
  };

  // Fonctions pour le formulaire d'ajout de lead
  const handleAddLead = () => {
    setShowAddLeadModal(true);
  };

  const handleCloseAddLead = () => {
    setShowAddLeadModal(false);
    // Reset du formulaire
    setNewLead({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      company: '',
      desiredArrival: '',
      desiredDeparture: '',
      destination: '',
      guestCount: '',
      children: '',
      budget: '',
      propertyType: '',
      amenities: [],
      purposeOfStay: '',
      source: '',
      notes: '',
      priority: 'medium',
      conciergeServices: [],
      specialRequests: ''
    });
  };

  const handleSubmitLead = (e) => {
    e.preventDefault();
    // Générer un nouvel ID
    const newId = `L-2024-${String(Math.floor(Math.random() * 1000)).padStart(3, '0')}`;
    
    // Créer le nouveau lead
    const leadToAdd = {
      id: newId,
      name: `${newLead.firstName} ${newLead.lastName}`,
      source: newLead.source || 'Site Web',
      destination: newLead.destination || 'Non spécifié',
      value: parseInt(newLead.budget) || 0,
      description: `${newLead.purposeOfStay} - ${newLead.guestCount} personnes`,
      status: newLead.priority === 'high' ? 'Hot' : newLead.priority === 'low' ? 'Cold' : 'Warm',
      lastActivity: new Date().toISOString().split('T')[0],
      aiScore: Math.floor(Math.random() * 40) + 60 // Score aléatoire entre 60-100
    };

    // Ajouter au stage "Premier Contact"
    if (leadsData) {
      setLeadsData(prevData => {
        const newData = {...prevData};
        newData['first-contact'] = newData['first-contact'] || [];
        newData['first-contact'].unshift(leadToAdd);
        return newData;
      });
    } else {
      leadsByStage['first-contact'] = leadsByStage['first-contact'] || [];
      leadsByStage['first-contact'].unshift(leadToAdd);
    }
    
    // Fermer le modal
    handleCloseAddLead();
    
    // Optionnel: message de confirmation
    console.log('Nouveau lead ajouté:', leadToAdd);
  };

  const handleInputChange = (field, value) => {
    setNewLead(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleArrayInputChange = (field, value) => {
    setNewLead(prev => ({
      ...prev,
      [field]: value.split(',').map(item => item.trim()).filter(item => item)
    }));
  };

  // Fonctions pour le drag & drop
  const handleDragStart = (e, leadId, fromStage) => {
    e.dataTransfer.setData('leadId', leadId);
    e.dataTransfer.setData('fromStage', fromStage);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, toStage) => {
    e.preventDefault();
    const leadId = e.dataTransfer.getData('leadId');
    const fromStage = e.dataTransfer.getData('fromStage');
    
    if (fromStage !== toStage && leadsData) {
      // Trouver le lead dans le stage source
      const lead = leadsData[fromStage]?.find(l => l.id === leadId);
      if (lead) {
        // Mettre à jour l'état
        setLeadsData(prevData => {
          const newData = {...prevData};
          
          // Supprimer du stage source
          newData[fromStage] = newData[fromStage].filter(l => l.id !== leadId);
          
          // Ajouter au stage destination
          newData[toStage] = newData[toStage] || [];
          newData[toStage].push(lead);
          
          return newData;
        });
        
        console.log(`Lead ${leadId} déplacé de ${fromStage} vers ${toStage}`);
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'VIP': return 'bg-purple-100 text-purple-800';
      case 'Hot': return 'bg-red-100 text-red-800';
      case 'Warm': return 'bg-yellow-100 text-yellow-800';
      case 'Cold': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStageColor = (color) => {
    const colors = {
      blue: 'bg-blue-50 border-blue-200',
      yellow: 'bg-yellow-50 border-yellow-200',
      orange: 'bg-orange-50 border-orange-200',
      purple: 'bg-purple-50 border-purple-200',
      green: 'bg-green-50 border-green-200'
    };
    return colors[color] || 'bg-gray-50 border-gray-200';
  };

  const LeadDetailsPanel = () => {
    if (!showDetailsPanel || !selectedLead) return null;

    // Trouver le lead dans tous les stages
    let lead = null;
    const currentLeadsData = leadsData || leadsByStage;
    for (const stage of Object.values(currentLeadsData)) {
      lead = stage.find(l => l.id === selectedLead);
      if (lead) break;
    }

    if (!lead) return null;

    return (
      <div className="fixed inset-y-0 right-0 w-1/3 bg-white border-l border-gray-200 shadow-xl z-40 overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-800">Détails du Lead</h3>
            <button
              onClick={() => setShowDetailsPanel(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={24} />
            </button>
          </div>

          {/* Informations Contact */}
          <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-semibold text-gray-800">{lead.name}</h4>
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(lead.status)}`}>
                {lead.status}
              </span>
            </div>
            
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">ID Lead:</span>
                <span className="font-medium">{lead.id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Source:</span>
                <span className="font-medium">{lead.source}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Destination:</span>
                <span className="font-medium">{lead.destination}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Valeur estimée:</span>
                <span className="font-medium text-green-600">€{lead.value.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Dernière activité:</span>
                <span className="font-medium">{lead.lastActivity}</span>
              </div>
            </div>
          </div>

          {/* Détails Réservation */}
          <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
            <h4 className="font-semibold text-gray-800 mb-3">Détails de la Demande</h4>
            <p className="text-sm text-gray-700 mb-4">{lead.description}</p>
            
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Dates souhaitées:</span>
                <p className="font-medium">15-22 Déc 2024</p>
              </div>
              <div>
                <span className="text-gray-600">Nombre de personnes:</span>
                <p className="font-medium">8 adultes, 2 enfants</p>
              </div>
              <div>
                <span className="text-gray-600">Budget indicatif:</span>
                <p className="font-medium">€25,000 - €35,000</p>
              </div>
              <div>
                <span className="text-gray-600">Type de villa:</span>
                <p className="font-medium">Luxe, vue mer</p>
              </div>
            </div>
          </div>

          {/* Préférences */}
          <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
            <h4 className="font-semibold text-gray-800 mb-3">Préférences</h4>
            <div className="space-y-2 text-sm">
              <div>
                <span className="text-gray-600">Services demandés:</span>
                <div className="flex flex-wrap gap-1 mt-1">
                  <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">Chef privé</span>
                  <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">Spa</span>
                  <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">Majordome</span>
                </div>
              </div>
              <div>
                <span className="text-gray-600">Équipements souhaités:</span>
                <div className="flex flex-wrap gap-1 mt-1">
                  <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs">Piscine chauffée</span>
                  <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs">Salle de sport</span>
                  <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs">Cinema</span>
                </div>
              </div>
            </div>
          </div>

          {/* Score IA */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
            <div className="flex items-center space-x-2 mb-2">
              <Zap className="text-purple-600" size={16} />
              <h4 className="font-semibold text-purple-800">Score IA de Conversion</h4>
            </div>
            
            <div className="flex items-center space-x-3">
              <div className="flex-1">
                <div className="w-full bg-purple-200 rounded-full h-2">
                  <div 
                    className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${lead.aiScore}%` }}
                  ></div>
                </div>
              </div>
              <span className="text-lg font-bold text-purple-600">{lead.aiScore}%</span>
            </div>
            
            <p className="text-sm text-purple-700 mt-2">
              {lead.aiScore >= 90 ? "Probabilité très élevée de conversion" :
               lead.aiScore >= 80 ? "Bonne probabilité de conversion" :
               lead.aiScore >= 70 ? "Probabilité modérée de conversion" :
               "Nécessite plus de qualification"}
            </p>
          </div>

          {/* Historique d'Activité */}
          <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
            <h4 className="font-semibold text-gray-800 mb-3">Historique d'Activité</h4>
            
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                <div>
                  <p className="text-sm font-medium text-gray-800">Demande initiale reçue</p>
                  <p className="text-xs text-gray-600">2024-09-10 14:30 • Via site web</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                <div>
                  <p className="text-sm font-medium text-gray-800">Premier contact établi</p>
                  <p className="text-xs text-gray-600">2024-09-10 16:45 • Appel téléphonique</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2"></div>
                <div>
                  <p className="text-sm font-medium text-gray-800">Proposition envoyée</p>
                  <p className="text-xs text-gray-600">2024-09-11 10:20 • Email avec brochure</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                <div>
                  <p className="text-sm font-medium text-gray-800">Rappel planifié</p>
                  <p className="text-xs text-gray-600">2024-09-15 14:00 • Appel de suivi</p>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-gray-800 mb-3">Actions</h4>
            
            <div className="space-y-2">
              <button className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50">
                <Mail size={16} />
                <span>Envoyer Email</span>
              </button>
              <button className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-green-600 border border-green-300 rounded-lg hover:bg-green-50">
                <Phone size={16} />
                <span>Appeler</span>
              </button>
              <button className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-purple-600 border border-purple-300 rounded-lg hover:bg-purple-50">
                <MessageSquare size={16} />
                <span>Message WhatsApp</span>
              </button>
              <button className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-yellow-600 border border-yellow-300 rounded-lg hover:bg-yellow-50">
                <Calendar size={16} />
                <span>Programmer Rdv</span>
              </button>
            </div>
            
            <div className="mt-4 pt-4 border-t border-gray-200">
              <button className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
                Faire Avancer dans le Pipeline
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const AddLeadModal = () => {
    if (!showAddLeadModal) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto m-4">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-t-lg">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold">Nouveau Lead de Luxe</h2>
                <p className="text-blue-100 mt-1">Créez un nouveau prospect premium</p>
              </div>
              <button
                onClick={handleCloseAddLead}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <X size={24} />
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmitLead} className="p-6">
            {/* Section 1: Informations de Contact */}
            <div className="mb-8">
              <div className="flex items-center mb-4">
                <div className="bg-blue-100 p-2 rounded-lg mr-3">
                  <Users className="text-blue-600" size={20} />
                </div>
                <h3 className="text-lg font-semibold text-gray-800">Informations de Contact</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Prénom *</label>
                  <input
                    type="text"
                    required
                    value={newLead.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Prénom du client"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nom *</label>
                  <input
                    type="text"
                    required
                    value={newLead.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Nom de famille"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                  <input
                    type="email"
                    required
                    value={newLead.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="email@example.com"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone</label>
                  <input
                    type="tel"
                    value={newLead.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="+33 6 12 34 56 78"
                  />
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Société / Organisation</label>
                  <input
                    type="text"
                    value={newLead.company}
                    onChange={(e) => handleInputChange('company', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Nom de l'entreprise (optionnel)"
                  />
                </div>
              </div>
            </div>

            {/* Section 2: Projet de Voyage */}
            <div className="mb-8">
              <div className="flex items-center mb-4">
                <div className="bg-green-100 p-2 rounded-lg mr-3">
                  <Calendar className="text-green-600" size={20} />
                </div>
                <h3 className="text-lg font-semibold text-gray-800">Projet de Voyage</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Arrivée souhaitée</label>
                  <input
                    type="date"
                    value={newLead.desiredArrival}
                    onChange={(e) => handleInputChange('desiredArrival', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Départ souhaité</label>
                  <input
                    type="date"
                    value={newLead.desiredDeparture}
                    onChange={(e) => handleInputChange('desiredDeparture', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Destination *</label>
                  <select
                    required
                    value={newLead.destination}
                    onChange={(e) => handleInputChange('destination', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Sélectionner destination</option>
                    <option value="Saint-Tropez">Saint-Tropez</option>
                    <option value="Cannes">Cannes</option>
                    <option value="Nice">Nice</option>
                    <option value="Monaco">Monaco</option>
                    <option value="Saint-Barthélemy">Saint-Barthélemy</option>
                    <option value="Mustique">Mustique</option>
                    <option value="Ibiza">Ibiza</option>
                    <option value="Mykonos">Mykonos</option>
                    <option value="Autre">Autre destination</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nombre d'adultes</label>
                  <input
                    type="number"
                    min="1"
                    max="20"
                    value={newLead.guestCount}
                    onChange={(e) => handleInputChange('guestCount', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Nombre d'adultes"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nombre d'enfants</label>
                  <input
                    type="number"
                    min="0"
                    max="10"
                    value={newLead.children}
                    onChange={(e) => handleInputChange('children', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Nombre d'enfants"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Objectif du séjour</label>
                  <select
                    value={newLead.purposeOfStay}
                    onChange={(e) => handleInputChange('purposeOfStay', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Sélectionner objectif</option>
                    <option value="Vacances familiales">Vacances familiales</option>
                    <option value="Lune de miel">Lune de miel</option>
                    <option value="Événement / Mariage">Événement / Mariage</option>
                    <option value="Voyage d'affaires">Voyage d'affaires</option>
                    <option value="Retraite / Wellness">Retraite / Wellness</option>
                    <option value="Célébration anniversaire">Célébration anniversaire</option>
                    <option value="Autre">Autre</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Section 3: Préférences Villa */}
            <div className="mb-8">
              <div className="flex items-center mb-4">
                <div className="bg-purple-100 p-2 rounded-lg mr-3">
                  <MapPin className="text-purple-600" size={20} />
                </div>
                <h3 className="text-lg font-semibold text-gray-800">Préférences Villa & Budget</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Budget estimé (€) *</label>
                  <select
                    required
                    value={newLead.budget}
                    onChange={(e) => handleInputChange('budget', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Sélectionner budget</option>
                    <option value="25000">€15K - €25K / semaine</option>
                    <option value="50000">€25K - €50K / semaine</option>
                    <option value="75000">€50K - €75K / semaine</option>
                    <option value="100000">€75K - €100K / semaine</option>
                    <option value="150000">€100K - €150K / semaine</option>
                    <option value="200000">€150K+ / semaine</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Type de villa</label>
                  <select
                    value={newLead.propertyType}
                    onChange={(e) => handleInputChange('propertyType', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Sélectionner type</option>
                    <option value="Villa moderne">Villa moderne</option>
                    <option value="Villa traditionnelle">Villa traditionnelle</option>
                    <option value="Château / Domaine">Château / Domaine</option>
                    <option value="Villa contemporaine">Villa contemporaine</option>
                    <option value="Propriété historique">Propriété historique</option>
                    <option value="Villa avec yacht">Villa avec yacht</option>
                  </select>
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Équipements souhaités</label>
                  <input
                    type="text"
                    value={newLead.amenities.join(', ')}
                    onChange={(e) => handleArrayInputChange('amenities', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="ex: Piscine chauffée, Spa, Salle de sport, Vue mer, Chef privé (séparer par des virgules)"
                  />
                </div>
              </div>
            </div>

            {/* Section 4: Services & Suivi */}
            <div className="mb-8">
              <div className="flex items-center mb-4">
                <div className="bg-yellow-100 p-2 rounded-lg mr-3">
                  <Star className="text-yellow-600" size={20} />
                </div>
                <h3 className="text-lg font-semibold text-gray-800">Services VIP & Suivi</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Source du lead *</label>
                  <select
                    required
                    value={newLead.source}
                    onChange={(e) => handleInputChange('source', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Sélectionner source</option>
                    <option value="Site Web">Site Web</option>
                    <option value="Référence client">Référence client</option>
                    <option value="Google Ads">Google Ads</option>
                    <option value="Instagram">Instagram</option>
                    <option value="Facebook">Facebook</option>
                    <option value="Partenaire">Partenaire</option>
                    <option value="Événement">Événement</option>
                    <option value="Magazine">Magazine</option>
                    <option value="Autre">Autre</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Priorité</label>
                  <select
                    value={newLead.priority}
                    onChange={(e) => handleInputChange('priority', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="low">Faible - Lead froid</option>
                    <option value="medium">Moyenne - Lead warm</option>
                    <option value="high">Élevée - Lead chaud</option>
                  </select>
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Services de conciergerie souhaités</label>
                  <input
                    type="text"
                    value={newLead.conciergeServices.join(', ')}
                    onChange={(e) => handleArrayInputChange('conciergeServices', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="ex: Chef privé, Majordome, Chauffeur, Garde d'enfants, Spa à domicile (séparer par des virgules)"
                  />
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Demandes spéciales / Notes</label>
                  <textarea
                    rows="4"
                    value={newLead.specialRequests}
                    onChange={(e) => handleInputChange('specialRequests', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                    placeholder="Allergies, restrictions alimentaires, demandes spécifiques, notes importantes..."
                  />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={handleCloseAddLead}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-colors font-medium"
              >
                Créer le Lead
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  const LeadCard = ({ lead, stageId }) => (
    <div 
      className="bg-white border border-gray-200 rounded-lg p-4 mb-3 hover:shadow-lg hover:border-blue-300 transition-all cursor-move transform hover:scale-[1.02]"
      draggable={true}
      onDragStart={(e) => handleDragStart(e, lead.id, stageId)}
      title="Glissez-déposez pour changer d'étape"
    >
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-medium text-gray-800">{lead.name}</h4>
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(lead.status)}`}>
          {lead.status}
        </span>
      </div>
      
      <p className="text-sm text-gray-600 mb-3">{lead.description}</p>
      
      <div className="flex items-center justify-between text-sm mb-3">
        <span className="text-gray-500">{lead.source}</span>
        <span className="font-medium text-green-600">€{lead.value.toLocaleString()}</span>
      </div>
      
      <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
        <div className="flex items-center space-x-1">
          <MapPin size={12} />
          <span>{lead.destination}</span>
        </div>
        <span>{lead.lastActivity}</span>
      </div>
      
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-1">
          <Zap className="text-purple-500" size={12} />
          <span className="text-xs font-medium text-purple-600">{lead.aiScore}% score</span>
        </div>
        
        <div className="flex items-center space-x-2">
          <button
            onClick={() => {
              setSelectedLead(lead.id);
              setShowDetailsPanel(true);
            }}
            className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
            title="Voir détails"
          >
            <Eye size={16} />
          </button>
          <button 
            className="p-1 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
            title="Envoyer email"
          >
            <Mail size={16} />
          </button>
          <button 
            className="p-1 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded transition-colors"
            title="Appeler"
          >
            <Phone size={16} />
          </button>
          <button 
            className="p-1 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded transition-colors"
            title="Envoyer message"
          >
            <MessageSquare size={16} />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="lead-management">
      {/* En-tête */}
      <div className="page-header bg-white border-b border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Gestion des Prospects</h1>
            <p className="text-gray-600 mt-1">Pipeline de conversion et suivi des leads pour villas de luxe</p>
          </div>
          
          <div className="flex items-center space-x-3 mt-4 lg:mt-0">
            <button className="flex items-center space-x-2 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
              <Filter size={18} />
              <span>Filtres Avancés</span>
            </button>
            <button className="flex items-center space-x-2 px-4 py-2 text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50">
              <Download size={18} />
              <span>Export Excel</span>
            </button>
            <button className="flex items-center space-x-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700">
              <Plus size={18} />
              <span>Nouveau Lead</span>
            </button>
          </div>
        </div>

        {/* Stats Rapides */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <Users className="text-blue-600" size={24} />
              <span className="text-sm font-medium text-blue-600">+12.3%</span>
            </div>
            <div className="text-2xl font-bold text-gray-800">{pipelineStats.activeLeads}</div>
            <div className="text-sm text-gray-600">Leads Actifs</div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="text-green-600" size={24} />
              <span className="text-sm font-medium text-green-600">+18.7%</span>
            </div>
            <div className="text-2xl font-bold text-gray-800">{pipelineStats.totalPipeline}</div>
            <div className="text-sm text-gray-600">Pipeline Total</div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="text-yellow-600" size={24} />
              <span className="text-sm font-medium text-yellow-600">+3.2%</span>
            </div>
            <div className="text-2xl font-bold text-gray-800">{pipelineStats.conversionRate}%</div>
            <div className="text-sm text-gray-600">Taux de Conversion</div>
          </div>

          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <Clock className="text-purple-600" size={24} />
              <span className="text-sm font-medium text-purple-600">-2.1j</span>
            </div>
            <div className="text-2xl font-bold text-gray-800">{pipelineStats.avgTime}</div>
            <div className="text-sm text-gray-600">Temps Moyen</div>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Insights IA */}
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-6 mb-6">
          <div className="flex items-center space-x-2 mb-4">
            <Zap className="text-purple-600" size={24} />
            <h3 className="text-lg font-semibold text-gray-800">Insights IA</h3>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div>
              <h4 className="font-medium text-green-800 mb-2">🔥 Opportunités Chaudes</h4>
              <ul className="space-y-1 text-sm text-green-700">
                {aiInsights.hotOpportunities.map((item, index) => (
                  <li key={index}>• {item}</li>
                ))}
              </ul>
            </div>
            
            <div>
              <h4 className="font-medium text-blue-800 mb-2">📈 Tendances Détectées</h4>
              <ul className="space-y-1 text-sm text-blue-700">
                {aiInsights.detectedTrends.map((item, index) => (
                  <li key={index}>• {item}</li>
                ))}
              </ul>
            </div>
            
            <div>
              <h4 className="font-medium text-red-800 mb-2">⚡ Actions Urgentes</h4>
              <ul className="space-y-1 text-sm text-red-700">
                {aiInsights.urgentActions.map((item, index) => (
                  <li key={index}>• {item}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Filtres */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <select 
              value={filterDestination}
              onChange={(e) => setFilterDestination(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
            >
              <option value="all">Toutes destinations</option>
              <option value="cannes">Cannes</option>
              <option value="saint-tropez">Saint-Tropez</option>
              <option value="monaco">Monaco</option>
              <option value="nice">Nice</option>
            </select>

            <select 
              value={filterSegment}
              onChange={(e) => setFilterSegment(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
            >
              <option value="all">Tous segments</option>
              <option value="vip">VIP</option>
              <option value="corporate">Corporate</option>
              <option value="famille">Famille</option>
              <option value="evenement">Événement</option>
            </select>

            <select 
              value={filterSource}
              onChange={(e) => setFilterSource(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
            >
              <option value="all">Toutes sources</option>
              <option value="site-web">Site Web</option>
              <option value="reference">Référence</option>
              <option value="publicite">Publicité</option>
              <option value="partenaire">Partenaire</option>
              <option value="social">Réseaux sociaux</option>
            </select>

            <input
              type="text"
              placeholder="Propriétaire assigné..."
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
            />
          </div>
        </div>

        {/* Pipeline Kanban */}
        <div className="pipeline-container">
          <div className="flex space-x-6 overflow-x-auto pb-6">
            {pipelineStages.map((stage) => (
              <div 
                key={stage.id} 
                className={`min-w-80 ${getStageColor(stage.color)} border rounded-lg p-4 transition-all hover:border-blue-400`}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, stage.id)}
                onDragEnter={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.add('border-blue-500', 'bg-blue-50');
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50');
                }}
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-800">{stage.name}</h3>
                  <div className="text-right">
                    <div className="text-sm font-medium text-gray-800">{stage.count} leads</div>
                    <div className="text-sm text-gray-600">{stage.value}</div>
                  </div>
                </div>
                
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {((leadsData || leadsByStage)[stage.id] || []).map((lead) => (
                    <LeadCard key={lead.id} lead={lead} stageId={stage.id} />
                  ))}
                </div>
                
                <button 
                  onClick={handleAddLead}
                  className="w-full mt-4 flex items-center justify-center space-x-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-gray-400 hover:text-gray-700 transition-colors"
                >
                  <Plus size={16} />
                  <span>Ajouter Lead</span>
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <LeadDetailsPanel />
      <AddLeadModal />
    </div>
  );
};

export default LeadManagement;